import type { SupabaseClient } from '../../lib/supabase';
import {
  ensureProjectFolder,
  shareFileWithLink,
  uploadFileToDrive,
  withDriveToken,
} from '../files/drive';
import type {
  CgOrder,
  CgOrderItem,
  CgOrderType,
  CgProductType,
  CgReportTemplate,
  CgStatus,
} from './types';
import type { Json } from '../../types/database.types';
import type { GateStep } from './financialGate';
import type { AssetDecision, OrderAsset } from './approvalPanel';
import type { OrderActivityRow } from './orderActivity';
import type { PoDocument, PoSnapshot } from './PoDocumentView';
import type { Certificate } from './CertificateView';
import type { BillLineInput, CgOrderCost } from './orderProfit';

/**
 * Database access for the CG Order Tracker. Every table sits behind the
 * cg_members gate in Row Level Security; nothing here is trusted.
 */

// One literal, deliberately unwrapped (PostgREST type inference).
// prettier-ignore
const ORDER_COLUMNS = 'id, order_no, order_no_confirmed, client, region, status, order_type, shipping_handling_usd, cost_override_usd, date_paid, invoice_sent_on, date_po_sent, date_acknowledged, date_shipped, tracking_number, ship_to, target_ship_date, sample_due_days, sample_clock_started_at, supplier_confirmed_at, supplier_confirmed_by, supplier_revision_note, supplier_revision_at, supplier_revision_ack_at, supplier_revision_ack_by, notes, project_id, company_id, lead_id, deal_id, sheet_id, created_by, owner_id, courier_name, supplier_ready_for_shipment, supplier_ready_at, supplier_ready_note, notes_to_manufacturer, date_delivered, tracking_url, production_due_days, production_clock_started_at, status_changed_at, created_at, updated_at';

// prettier-ignore
const ITEM_COLUMNS = 'id, order_id, product_type, description, units, unit_price_usd, unit_cost_usd, sku, spec, sheet_id, created_at';

const STATUS_COLUMNS = 'id, sort_order, is_open, is_archived, order_type, label';
const PRODUCT_TYPE_COLUMNS = 'id, sort_order';
const TEMPLATE_COLUMNS = 'id, name, config, created_by, created_at';

function fail(message: string): never {
  throw new Error(message);
}

export async function fetchCgAccess(client: SupabaseClient): Promise<boolean> {
  const { data, error } = await client.rpc('is_cg_member');
  if (error) {
    fail(error.message);
  }
  return data === true;
}

/**
 * The stages of ONE pipeline, never both at once.
 *
 * 20261208000100 put a second ladder in cg_statuses. Five screens render this
 * list — the board's columns, two status dropdowns, a filter and a report
 * grouping — and every one of them would have grown six empty In-Stock columns
 * the moment that migration was applied.
 *
 * Filtering here rather than at those five call sites means the live CG board
 * looks exactly as it did before, and the pipeline switcher in the next drop
 * changes what is passed in rather than what each screen does with it.
 *
 * The default is 'custom' deliberately: every existing caller keeps today's
 * behaviour without being edited.
 */
export async function fetchCgStatuses(
  client: SupabaseClient,
  pipeline: CgOrderType | 'all' = 'custom',
): Promise<CgStatus[]> {
  // 'all' loads both ladders, for the Orders table under All: each row's
  // status dropdown has to offer its OWN stages, and a row cannot be given the
  // other pipeline's without the database refusing the change on click.
  const base = client.from('cg_statuses').select(STATUS_COLUMNS);
  const scoped = pipeline === 'all' ? base : base.eq('order_type', pipeline);
  const { data, error } = await scoped.order('sort_order', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchCgProductTypes(client: SupabaseClient): Promise<CgProductType[]> {
  const { data, error } = await client
    .from('cg_product_types')
    .select(PRODUCT_TYPE_COLUMNS)
    .order('sort_order', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchCgOrders(client: SupabaseClient): Promise<CgOrder[]> {
  const { data, error } = await client
    .from('cg_orders')
    .select(ORDER_COLUMNS)
    .order('created_at', { ascending: false });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchCgItems(client: SupabaseClient): Promise<CgOrderItem[]> {
  const { data, error } = await client
    .from('cg_order_items')
    .select(ITEM_COLUMNS)
    .order('created_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

// prettier-ignore
const COST_COLUMNS = 'id, order_id, label, amount_usd, position, created_at, created_by';

/**
 * Every supplier bill line, for every order. The orders list, the order page,
 * the report and the name suggestions all read from this one set, the same way
 * fetchCgItems serves line items. CG is a handful of orders a quarter.
 */
export async function fetchCgOrderCosts(client: SupabaseClient): Promise<CgOrderCost[]> {
  const { data, error } = await client
    .from('cg_order_costs')
    .select(COST_COLUMNS)
    .order('order_id', { ascending: true })
    .order('position', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

/** Replace an order's supplier bill in one statement (20270162000100). */
export async function setOrderCosts(
  client: SupabaseClient,
  orderId: string,
  lines: BillLineInput[],
): Promise<void> {
  const { error } = await client.rpc('set_order_costs', {
    p_order: orderId,
    p_lines: lines as unknown as Json,
  });
  if (error) {
    fail(error.message);
  }
}

export async function fetchShipTargetDays(client: SupabaseClient): Promise<number> {
  const { data, error } = await client
    .from('cg_settings')
    .select('key, value')
    .eq('key', 'ship_target_days')
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  const value = data?.value;
  return typeof value === 'number' ? value : 21;
}

export async function updateShipTargetDays(client: SupabaseClient, days: number): Promise<void> {
  const { error } = await client
    .from('cg_settings')
    .update({ value: days })
    .eq('key', 'ship_target_days');
  if (error) {
    fail(error.message);
  }
}

export interface CgItemInput {
  productType: string;
  description: string;
  units: number;
  unitPriceUsd: number;
  unitCostUsd: number | null;
  /**
   * The two the Production PO prints per line. Columns since
   * 20260920000100, but nothing wrote them until the form grew fields, which
   * is why POs printed lines with no SKU and no spec.
   */
  sku: string;
  spec: string;
}

export interface CgOrderInput {
  orderNo: string;
  client: string;
  /**
   * The CRM ids, set only by the New Client Link wizard. Optional because
   * every other path into this form takes a typed client name, and an order
   * may legitimately name somebody who is not in the CRM at all — which is why
   * `client` above stays authoritative for display.
   */
  companyId?: string;
  leadId?: string;
  region: string;
  /**
   * Which pipeline this order runs on. Optional so every existing caller —
   * the supplier handoff, the New Client Link wizard, the deal handoff —
   * keeps raising custom orders without being edited.
   */
  orderType?: CgOrderType;
  status: string;
  shippingHandlingUsd: number;
  costOverrideUsd: number | null;
  datePaid: string | null;
  datePoSent: string | null;
  dateAcknowledged: string | null;
  dateShipped: string | null;
  trackingNumber: string | null;
  /** Printed as the SHIP TO block on the Production PO. One free-text block. */
  shipTo: string;
  /** The date the factory is asked to hit. Printed on the PO. */
  targetShipDate: string | null;
  /** Days the supplier gets for samples once they confirm. */
  sampleDueDays: number;
  notes: string;
  /** Build instructions for the factory. Printed on the PO; `notes` is not. */
  notesToManufacturer: string;
  items: CgItemInput[];
}

export async function createCgOrder(
  client: SupabaseClient,
  actorId: string,
  input: CgOrderInput,
): Promise<CgOrder> {
  const { data, error } = await client
    .from('cg_orders')
    .insert({
      order_no: input.orderNo.trim(),
      client: input.client.trim(),
      company_id: input.companyId ?? null,
      lead_id: input.leadId ?? null,
      region: input.region,
      order_type: input.orderType ?? 'custom',
      status: input.status,
      shipping_handling_usd: input.shippingHandlingUsd,
      cost_override_usd: input.costOverrideUsd,
      date_paid: input.datePaid,
      date_po_sent: input.datePoSent,
      date_acknowledged: input.dateAcknowledged,
      date_shipped: input.dateShipped,
      tracking_number: input.trackingNumber,
      ship_to: input.shipTo.trim(),
      target_ship_date: input.targetShipDate,
      sample_due_days: input.sampleDueDays,
      notes: input.notes.trim(),
      notes_to_manufacturer: input.notesToManufacturer.trim(),
      created_by: actorId,
    })
    .select(ORDER_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  if (input.items.length > 0) {
    const { error: itemsError } = await client.from('cg_order_items').insert(
      input.items.map((item) => ({
        order_id: data.id,
        product_type: item.productType,
        description: item.description.trim(),
        units: item.units,
        unit_price_usd: item.unitPriceUsd,
        unit_cost_usd: item.unitCostUsd,
        sku: item.sku.trim(),
        spec: item.spec.trim(),
      })),
    );
    if (itemsError) {
      fail(itemsError.message);
    }
  }
  await logCgActivity(client, actorId, data.id, 'created', { order_no: data.order_no });
  return data;
}

export interface CgOrderUpdate {
  client?: string;
  region?: string;
  status?: string;
  shippingHandlingUsd?: number;
  costOverrideUsd?: number | null;
  datePaid?: string | null;
  datePoSent?: string | null;
  dateAcknowledged?: string | null;
  dateShipped?: string | null;
  trackingNumber?: string | null;
  shipTo?: string;
  targetShipDate?: string | null;
  sampleDueDays?: number;
  notes?: string;
  notesToManufacturer?: string;
  /**
   * Null clears the owner rather than leaving it alone.
   *
   * undefined means "not part of this update"; null means "nobody owns this".
   * They have to be different, because unassigning is a real action — and
   * cg_order_owner() then falls back to created_by rather than the order
   * becoming unnotifiable.
   */
  ownerId?: string | null;
}

export async function updateCgOrder(
  client: SupabaseClient,
  actorId: string,
  orderId: string,
  update: CgOrderUpdate,
): Promise<CgOrder> {
  const patch: {
    client?: string;
    region?: string;
    status?: string;
    shipping_handling_usd?: number;
    cost_override_usd?: number | null;
    date_paid?: string | null;
    date_po_sent?: string | null;
    date_acknowledged?: string | null;
    date_shipped?: string | null;
    tracking_number?: string | null;
    ship_to?: string;
    target_ship_date?: string | null;
    sample_due_days?: number;
    notes?: string;
    notes_to_manufacturer?: string;
    owner_id?: string | null;
  } = {};
  if (update.client !== undefined) patch.client = update.client.trim();
  if (update.region !== undefined) patch.region = update.region;
  if (update.status !== undefined) patch.status = update.status;
  if (update.shippingHandlingUsd !== undefined)
    patch.shipping_handling_usd = update.shippingHandlingUsd;
  if (update.costOverrideUsd !== undefined) patch.cost_override_usd = update.costOverrideUsd;
  if (update.datePaid !== undefined) patch.date_paid = update.datePaid;
  if (update.datePoSent !== undefined) patch.date_po_sent = update.datePoSent;
  if (update.dateAcknowledged !== undefined) patch.date_acknowledged = update.dateAcknowledged;
  if (update.dateShipped !== undefined) patch.date_shipped = update.dateShipped;
  if (update.trackingNumber !== undefined) patch.tracking_number = update.trackingNumber;
  if (update.shipTo !== undefined) patch.ship_to = update.shipTo.trim();
  if (update.targetShipDate !== undefined) patch.target_ship_date = update.targetShipDate;
  if (update.sampleDueDays !== undefined) patch.sample_due_days = update.sampleDueDays;
  if (update.notes !== undefined) patch.notes = update.notes.trim();
  if (update.notesToManufacturer !== undefined)
    patch.notes_to_manufacturer = update.notesToManufacturer.trim();
  if (update.ownerId !== undefined) patch.owner_id = update.ownerId;
  const { data, error } = await client
    .from('cg_orders')
    .update(patch)
    .eq('id', orderId)
    .select(ORDER_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  await logCgActivity(client, actorId, orderId, 'updated', { fields: Object.keys(patch) });
  return data;
}

export async function replaceCgItems(
  client: SupabaseClient,
  orderId: string,
  items: CgItemInput[],
): Promise<void> {
  const { error: deleteError } = await client
    .from('cg_order_items')
    .delete()
    .eq('order_id', orderId)
    .is('sheet_id', null);
  if (deleteError) {
    fail(deleteError.message);
  }
  const { error: legacyError } = await client
    .from('cg_order_items')
    .delete()
    .eq('order_id', orderId)
    .not('sheet_id', 'is', null);
  if (legacyError) {
    fail(legacyError.message);
  }
  if (items.length > 0) {
    const { error } = await client.from('cg_order_items').insert(
      items.map((item) => ({
        order_id: orderId,
        product_type: item.productType,
        description: item.description.trim(),
        units: item.units,
        unit_price_usd: item.unitPriceUsd,
        unit_cost_usd: item.unitCostUsd,
        sku: item.sku.trim(),
        spec: item.spec.trim(),
      })),
    );
    if (error) {
      fail(error.message);
    }
  }
}

/**
 * Delete an order through the recorded path.
 *
 * Not a plain `.delete()` any more. That could never remove a signed-off
 * order, and — until 20261014000100 — could not remove an ordinary one either,
 * because the activity log refused to detach from it. The function does the
 * whole job in the database: writes who deleted what and why, drops the
 * certificate when there is one, and removes the order.
 *
 * `reason` is only required for an order a client signed off, and the database
 * decides that, not this call.
 */
/**
 * What the client has done with this order's proofs.
 *
 * Two round trips rather than one embedded query: asset_decisions carries no
 * order id, only an asset id, so the assets have to be known before their
 * decisions can be asked for. Fetching both and joining in the browser keeps
 * the SQL something anybody can read.
 *
 * CG members hold plain SELECT on both tables (20260929000100), so this needs
 * no function and no new grant — it is simply the first screen in the app to
 * look.
 */
export async function fetchOrderApproval(
  client: SupabaseClient,
  orderId: string,
): Promise<{ assets: OrderAsset[]; decisions: AssetDecision[] }> {
  const { data: assets, error: assetError } = await client
    .from('order_assets')
    .select(
      'id, cg_order_id, component_type, version, url, drive_file_id, content_hash, state, created_by, created_at, updated_at',
    )
    .eq('cg_order_id', orderId)
    .order('version', { ascending: false });
  if (assetError) {
    fail(assetError.message);
  }
  const rows = assets ?? [];
  if (rows.length === 0) {
    return { assets: [], decisions: [] };
  }

  const { data: decisions, error: decisionError } = await client
    .from('asset_decisions')
    .select('id, asset_id, decision, note, decided_at, decided_ip')
    .in(
      'asset_id',
      rows.map((row) => row.id),
    )
    .order('decided_at', { ascending: false });
  if (decisionError) {
    fail(decisionError.message);
  }

  return { assets: rows, decisions: decisions ?? [] };
}

export async function deleteCgOrder(
  client: SupabaseClient,
  orderId: string,
  reason = '',
): Promise<void> {
  const { error } = await client.rpc('delete_cg_order', {
    p_order: orderId,
    p_reason: reason,
  });
  if (error) {
    fail(error.message);
  }
}

async function logCgActivity(
  client: SupabaseClient,
  actorId: string,
  orderId: string,
  event: string,
  details: Record<string, unknown>,
): Promise<void> {
  const { error } = await client.from('cg_activity').insert({
    order_id: orderId,
    actor_id: actorId,
    event,
    details: details as Json,
  });
  if (error) {
    fail(error.message);
  }
}

export async function fetchCgTemplates(client: SupabaseClient): Promise<CgReportTemplate[]> {
  const { data, error } = await client
    .from('cg_report_templates')
    .select(TEMPLATE_COLUMNS)
    .order('created_at', { ascending: true });
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function saveCgTemplate(
  client: SupabaseClient,
  actorId: string,
  name: string,
  config: Json,
): Promise<CgReportTemplate> {
  const { data, error } = await client
    .from('cg_report_templates')
    .insert({ name: name.trim(), config, created_by: actorId })
    .select(TEMPLATE_COLUMNS)
    .single();
  if (error) {
    fail(error.message);
  }
  return data;
}

export async function deleteCgTemplate(client: SupabaseClient, templateId: string): Promise<void> {
  const { error } = await client.from('cg_report_templates').delete().eq('id', templateId);
  if (error) {
    fail(error.message);
  }
}

/**
 * The live review link for an order, or null.
 *
 * Read rather than minted. The wizard shows the URL once, at creation, and
 * nothing showed it again — so the person who has just re-uploaded a revised
 * proof had no way to get the link short of making a second one, which would
 * leave two live tokens on the same order.
 *
 * Revoked and expired tokens are excluded here rather than filtered in the
 * browser: an expired link pasted to a client is worse than no link, because
 * it looks like TAP sent something broken.
 */
export interface ActiveReviewToken {
  token: string;
  /** So the panel can say when it lapses, rather than only that it works. */
  expiresAt: string;
}

export async function fetchActiveReviewToken(
  client: SupabaseClient,
  orderId: string,
): Promise<ActiveReviewToken | null> {
  const { data, error } = await client
    .from('review_tokens')
    .select('token, expires_at, revoked_at')
    .eq('cg_order_id', orderId)
    .is('revoked_at', null)
    .gt('expires_at', new Date().toISOString())
    .order('expires_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    // Not fatal. The notice button simply does not appear; the order page must
    // still open for somebody who cannot read tokens.
    return null;
  }
  if (data === null) {
    return null;
  }
  return { token: data.token, expiresAt: data.expires_at };
}

/**
 * Replace an order's review link with a fresh one, on the same order.
 *
 * The reason this exists at all: before 20261023000100, an expired link could
 * only be replaced by running the client-link wizard again, which creates a
 * SECOND ORDER. Two orders for one job, both counted in every revenue and
 * margin report.
 */
export async function reissueReviewToken(
  client: SupabaseClient,
  orderId: string,
  days: number,
): Promise<string> {
  const { data, error } = await client.rpc('reissue_review_token', {
    p_order: orderId,
    p_days: days,
  });
  if (error) {
    fail(error.message);
  }
  // reissue_review_token RETURNS public.review_tokens — a composite, which
  // PostgREST hands back as a single object, not the array a SETOF would give.
  // Narrowed rather than asserted so a shape change surfaces as a thrown error
  // here instead of `undefined` reaching the clipboard as the client's link.
  const token = (data as { token?: unknown } | null)?.token;
  if (typeof token !== 'string' || token === '') {
    fail('review_token_not_returned');
  }
  return token;
}

/** Kill an order's live review link. Returns how many were revoked. */
export async function revokeReviewToken(
  client: SupabaseClient,
  orderId: string,
  reason: string,
): Promise<number> {
  const { data, error } = await client.rpc('revoke_review_token', {
    p_order: orderId,
    p_reason: reason,
  });
  if (error) {
    fail(error.message);
  }
  return typeof data === 'number' ? data : 0;
}

/** The two financial gate tasks for an order, with their completion state. */
export interface GateTask {
  id: string;
  title: string;
  slug: string;
  isCompleted: boolean;
}

/**
 * An order's financial gate tasks.
 *
 * Read through the tasks table, which is behind project RLS — so somebody who
 * cannot see the project sees an empty list rather than a leak. That is the
 * right failure: the banner then says nothing, and the database still refuses
 * the promotion.
 */
export async function fetchGateTasks(client: SupabaseClient, orderId: string): Promise<GateTask[]> {
  const { data, error } = await client
    .from('tasks')
    .select('id, title, cg_gate_slug, is_completed')
    .eq('cg_gate_order_id', orderId)
    .order('title', { ascending: true });
  if (error) {
    return [];
  }
  return (data ?? []).map((row) => ({
    id: row.id,
    title: row.title,
    slug: row.cg_gate_slug ?? '',
    isCompleted: row.is_completed,
  }));
}

/**
 * Move an order past the financial gate, as a TAP admin, with a reason.
 *
 * A single RPC rather than "set a flag then update": the reason has to reach
 * the trigger in the same transaction as the write, and the session setting it
 * travels in must be cleared afterwards or it waves through the next update on
 * a pooled connection.
 */
export async function overrideFinancialGate(
  client: SupabaseClient,
  input: { orderId: string; status: string; reason: string },
): Promise<void> {
  const { error } = await client.rpc('cg_override_financial_gate', {
    p_order: input.orderId,
    p_status: input.status,
    p_reason: input.reason,
  });
  if (error) {
    fail(error.message);
  }
}

/**
 * One order's recorded history.
 *
 * No migration needed for this: cg_activity_read has granted CG members SELECT
 * since 20260822000100. The trail was reachable the whole time and nothing
 * ever asked for it.
 *
 * Capped, because an order that has been edited fifty times does not need
 * fifty rows on screen to answer "what happened to it". Ordered in the
 * database rather than trusted to the index, which is on order_id.
 */
const ACTIVITY_LIMIT = 100;

export async function fetchOrderActivity(
  client: SupabaseClient,
  orderId: string,
): Promise<OrderActivityRow[]> {
  const { data, error } = await client
    .from('cg_activity')
    .select('id, event, details, actor_id, created_at')
    .eq('order_id', orderId)
    .order('created_at', { ascending: false })
    .limit(ACTIVITY_LIMIT);
  if (error) {
    fail(error.message);
  }
  return (data ?? []).map((row) => ({
    id: row.id,
    event: row.event,
    // details is jsonb: the generated type is Json, which includes scalars and
    // arrays. Anything that is not an object becomes an empty one rather than
    // reaching describeActivity, where `details['reason']` on a string would
    // read a character.
    details:
      typeof row.details === 'object' && row.details !== null && !Array.isArray(row.details)
        ? (row.details as Record<string, unknown>)
        : {},
    actor_id: row.actor_id,
    created_at: row.created_at,
  }));
}

/**
 * Whether a client has signed this order off.
 *
 * The RPC, not a derivation from asset states. cg_orders_guard_client_approval
 * checks for a signoff_certificates row, and a browser that decided the same
 * question a different way would eventually disagree with the trigger — an
 * order with no components at all reads as "everything approved" through the
 * assets and as "unapproved" through the certificate. One fact, one source.
 */
export async function fetchClientApproved(
  client: SupabaseClient,
  orderId: string,
): Promise<boolean> {
  const { data, error } = await client.rpc('cg_order_client_approved', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
  return data === true;
}

/**
 * Every PO issued for a job, newest first.
 *
 * Two functions rather than one with a role branch inside it — see the
 * migration. The supplier's list is fetched by the portal through its own RPC,
 * so a field added for TAP cannot reach a factory by default.
 */
export async function fetchPoDocuments(
  client: SupabaseClient,
  orderId: string,
): Promise<PoDocument[]> {
  const { data, error } = await client.rpc('cg_order_po_documents', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
  return (data ?? []).map((row) => ({
    revision: row.revision,
    label: row.label,
    issued_at: row.issued_at,
    superseded_at: row.superseded_at,
    reason: row.reason,
    snapshot: (typeof row.snapshot === 'object' && row.snapshot !== null
      ? row.snapshot
      : {}) as PoSnapshot,
  }));
}

/** The client's certificate of approval, as TAP sees it (with the signatory). */
export async function fetchCertificate(
  client: SupabaseClient,
  orderId: string,
): Promise<Certificate | null> {
  const { data, error } = await client.rpc('cg_order_certificate', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }
  return data as unknown as Certificate;
}

/** Active supplier organisations, for the order drawer's link picker. */
export async function fetchSupplierOptions(
  client: SupabaseClient,
): Promise<{ id: string; name: string }[]> {
  const { data, error } = await client.rpc('cg_supplier_options');
  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * Point an order at a supplier.
 *
 * The link lives on supplier_handoffs, not on cg_orders — supplier_org_for_order
 * and every supplier-facing function already read it there, and a second column
 * would be a second truth to keep in step.
 */
export async function linkOrderToSupplier(
  client: SupabaseClient,
  orderId: string,
  organizationId: string,
): Promise<void> {
  const { error } = await client.rpc('link_order_to_supplier', {
    p_order: orderId,
    p_org: organizationId,
  });
  if (error) {
    fail(error.message);
  }
}

/** The factory an order is linked to, or null when nothing is linked yet. */
export interface OrderSupplier {
  organizationId: string;
  organizationName: string;
  handoffId: string;
}

/**
 * Which supplier will receive this order's PO.
 *
 * Null is the ordinary state of a new order, not an error — so this returns
 * null rather than throwing on an empty result. The distinction matters: the
 * drawer needs to say "not linked yet" as a fact, and a thrown error would
 * render as "could not load" and teach people to ignore it.
 */
/** The MANUFACTURER block for the PO preview — name, attn, address. */
export interface SupplierLetterhead {
  name: string;
  attn: string;
  address: string;
}

/**
 * The same letterhead issue_po_revision freezes, read live for the preview.
 *
 * Through the RPC rather than a select on supplier_organizations, because the
 * preview must match the frozen sheet FIELD FOR FIELD — including the
 * 'Production Lead' fallback — and two implementations of that fallback would
 * eventually disagree on some factory with a blank contact name.
 */
export async function fetchSupplierLetterhead(
  client: SupabaseClient,
  organizationId: string,
): Promise<SupplierLetterhead | null> {
  const { data, error } = await client.rpc('cg_supplier_letterhead', { p_org: organizationId });
  if (error) {
    fail(error.message);
  }
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }
  const row = data as Record<string, unknown>;
  return {
    name: typeof row['name'] === 'string' ? row['name'] : '',
    attn: typeof row['attn'] === 'string' ? row['attn'] : '',
    address: typeof row['address'] === 'string' ? row['address'] : '',
  };
}

export async function fetchOrderSupplier(
  client: SupabaseClient,
  orderId: string,
): Promise<OrderSupplier | null> {
  const { data, error } = await client.rpc('cg_order_supplier', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
  const row = (data ?? [])[0];
  if (row === undefined) {
    return null;
  }
  return {
    organizationId: row.organization_id,
    organizationName: row.organization_name,
    handoffId: row.handoff_id,
  };
}

/**
 * Record that somebody at TAP has dealt with a supplier's revision request.
 *
 * Not a note edit and not a status change — the fix for a revision request is
 * to change the order, which the drawer around this already does. This marks
 * the question answered, which is the one part the system cannot infer: a
 * target date that moved might have moved for reasons that have nothing to do
 * with the factory.
 */
export async function acknowledgeSupplierRevision(
  client: SupabaseClient,
  orderId: string,
): Promise<void> {
  const { error } = await client.rpc('acknowledge_supplier_revision', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
}

// ── The financial gate workflow (Batch 11a) ────────────────────────────────

/**
 * The checklist for one order.
 *
 * Throws rather than returning [] on error, unlike fetchGateTasks. An empty
 * list is a real answer here — it means "no steps configured", which the gate
 * reads as clear — so swallowing a failure into the same shape would let a
 * permission problem look like a green light.
 */
export async function fetchGateState(client: SupabaseClient, orderId: string): Promise<GateStep[]> {
  const { data, error } = await client.rpc('cg_gate_state', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

export async function setGateStep(
  client: SupabaseClient,
  input: { orderId: string; slug: string; done: boolean },
): Promise<void> {
  const { error } = await client.rpc('cg_set_gate_step', {
    p_order: input.orderId,
    p_slug: input.slug,
    p_done: input.done,
  });
  if (error) {
    fail(error.message);
  }
}

/**
 * Stamps invoice_sent_on. Ticks no gate step — sending is not being paid.
 *
 * The date is passed rather than left to the server's default, because finance
 * usually emails the invoice a day or two before anybody records it and
 * "today" would be the wrong answer more often than the right one.
 */
export async function markInvoiceSent(
  client: SupabaseClient,
  input: { orderId: string; on: string },
): Promise<void> {
  const { error } = await client.rpc('cg_mark_invoice_sent', {
    p_order: input.orderId,
    p_sent_on: input.on,
  });
  if (error) {
    fail(error.message);
  }
}

/** Ticks the invoice step and stamps date_paid in one transaction. */
export async function markInvoicePaid(client: SupabaseClient, orderId: string): Promise<void> {
  const { error } = await client.rpc('cg_mark_invoice_paid', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
}

/** Advances the order and snapshots the approved proofs. */
export async function sendPoToSupplier(client: SupabaseClient, orderId: string): Promise<void> {
  const { error } = await client.rpc('cg_send_po_to_supplier', { p_order: orderId });
  if (error) {
    fail(error.message);
  }
}

// ── The client's logo ────────────────────────────────────────────────────
//
// Kyu, 2 Sep: "we need to add 1 more type of file (not included from what the
// client would approve). It's Client's Logo." One per order; the factory sees
// it wherever it sees the approved proofs. It is a reference file, not a
// component — see migration 20270105000100 for why that distinction matters.

/** The same Drive folder the proofs go to; one folder, not a second one. */
const CLIENT_PROOF_FOLDER = 'TAP Client Proofs';

export interface OrderReferenceFile {
  id: string;
  cg_order_id: string;
  kind: string;
  version: number;
  url: string;
  drive_file_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** What the file is, in the uploader's words. Empty for the client logo. */
  label: string;
}

/** The three slots, in the order they are shown. */
export const REFERENCE_KINDS = ['client_logo', 'extra_1', 'extra_2'] as const;
export type ReferenceKind = (typeof REFERENCE_KINDS)[number];

const REFERENCE_COLUMNS =
  'id, cg_order_id, kind, label, version, url, drive_file_id, created_by, created_at, updated_at';

/**
 * Every reference file on an order, not just the logo.
 *
 * Ced, 4 September: the packaging file for ES "had to be sent to Jen directly
 * through WhatsApp instead", because the screen had one slot and it was
 * already spoken for. Two more, optional and named.
 */
export async function fetchOrderReferenceFiles(
  client: SupabaseClient,
  orderId: string,
): Promise<OrderReferenceFile[]> {
  const { data, error } = await client
    .from('order_reference_files')
    .select(REFERENCE_COLUMNS)
    .eq('cg_order_id', orderId);
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchOrderClientLogo(
  client: SupabaseClient,
  orderId: string,
): Promise<OrderReferenceFile | null> {
  const { data, error } = await client
    .from('order_reference_files')
    .select(REFERENCE_COLUMNS)
    .eq('cg_order_id', orderId)
    .eq('kind', 'client_logo')
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  return data;
}

/**
 * Drive first, database second — the same order uploadRevisedProof uses, for
 * the same reason: if the upload fails there is no row pointing at nothing.
 * The database replaces rather than adds, so calling this twice is how a logo
 * gets corrected.
 */
export async function setOrderClientLogo(
  client: SupabaseClient,
  input: { orderId: string; file: File },
): Promise<OrderReferenceFile> {
  const uploaded = await withDriveToken(client, async (token) => {
    const folderId = await ensureProjectFolder(token, 'root', CLIENT_PROOF_FOLDER);
    const created = await uploadFileToDrive(token, folderId, input.file);
    await shareFileWithLink(token, created.id);
    return {
      url: created.webViewLink ?? `https://drive.google.com/file/d/${created.id}/view`,
      driveFileId: created.id,
    };
  });

  const { data, error } = await client.rpc('set_order_client_logo', {
    p_order: input.orderId,
    p_url: uploaded.url,
    p_drive_file_id: uploaded.driveFileId,
  });
  if (error) {
    fail(error.message);
  }
  return data;
}

/**
 * One of the two extra slots.
 *
 * Same Drive-first order as the logo above, for the same reason: if the upload
 * fails there is no row pointing at nothing. The label is what the factory
 * reads — a slot called 'extra_1' tells Jen nothing, which is the problem this
 * whole change exists to fix.
 */
export async function setOrderReferenceFile(
  client: SupabaseClient,
  input: { orderId: string; kind: ReferenceKind; label: string; file: File },
): Promise<OrderReferenceFile> {
  const uploaded = await withDriveToken(client, async (token) => {
    const folderId = await ensureProjectFolder(token, 'root', CLIENT_PROOF_FOLDER);
    const created = await uploadFileToDrive(token, folderId, input.file);
    await shareFileWithLink(token, created.id);
    return {
      url: created.webViewLink ?? `https://drive.google.com/file/d/${created.id}/view`,
      driveFileId: created.id,
    };
  });

  const { data, error } = await client.rpc('set_order_reference_file', {
    p_order: input.orderId,
    p_kind: input.kind,
    p_url: uploaded.url,
    p_label: input.label,
    p_drive_file_id: uploaded.driveFileId,
  });
  if (error) {
    fail(error.message);
  }
  return data;
}

/**
 * Takes a file off an order.
 *
 * Only the extra slots, and only before a PO exists — the database refuses
 * both. A file attached to the wrong order has to come off it; the alternative
 * people reach for is uploading a blank page over the top, which leaves the
 * factory a blank page.
 */
export async function clearOrderReferenceFile(
  client: SupabaseClient,
  input: { orderId: string; kind: ReferenceKind },
): Promise<void> {
  const { error } = await client.rpc('clear_order_reference_file', {
    p_order: input.orderId,
    p_kind: input.kind,
  });
  if (error) {
    fail(error.message);
  }
}

/**
 * The Shopify order number, set once Shopify has one.
 *
 * A CG order is created so the client can be sent a review link, and Kyu:
 * "We only create the shopify order once the Client approves the design." So
 * the number the Hub writes at creation is a placeholder for a fact that does
 * not exist yet, and this is how the real one lands on it afterwards.
 *
 * Everything that matters is in the database, not here:
 * set_shopify_order_no() refuses once a PO has been issued, requires a reason,
 * and writes the change to cg_activity. A trigger refuses any other route.
 * This function only carries the three values and lets the refusal through.
 */
export async function setShopifyOrderNo(
  client: SupabaseClient,
  input: { orderId: string; orderNo: string; reason: string },
): Promise<void> {
  const { error } = await client.rpc('set_shopify_order_no', {
    p_order: input.orderId,
    p_order_no: input.orderNo.trim(),
    p_reason: input.reason.trim(),
  });
  if (error) throw new Error(error.message);
}

/**
 * The refusals set_shopify_order_no can raise, in words for the person who
 * pressed the button.
 *
 * Passed through unchanged when unrecognised, for the reason explainFailure()
 * gives in NewClientLinkDialog: a wrong-but-warm message sends somebody
 * looking in the wrong place and hides the string a developer needed.
 */
export function shopifyOrderNoError(message: string): string {
  if (message.includes('po_already_issued')) {
    return 'A purchase order has already been issued for this order, so its number is fixed. The factory holds a sheet with it printed on. Ask Kyu if it genuinely has to change.';
  }
  if (message.includes('order_no_taken')) {
    return 'Another order already has that number.';
  }
  if (message.includes('order_no_required')) {
    return 'Enter the Shopify order number.';
  }
  if (message.includes('reason_required')) {
    return 'Say why it is changing — it goes on the order’s history.';
  }
  if (message.includes('order_not_found')) {
    return 'That order no longer exists. Reload the page.';
  }
  if (message.includes('not_authorized_for_cg_orders')) {
    return 'You do not have access to CG orders.';
  }
  if (message.includes('order_no_is_set_by_function')) {
    return 'The order number cannot be edited directly. Use this box, which records the change.';
  }
  return message;
}
